import React from 'react';

let did = false;
const ensure = () => {
  if (did || typeof document === 'undefined') return;
  did = true;
  const s = document.createElement('style');
  s.dataset.pl = 'toast';
  s.textContent = `
.pl-toasts{position:fixed;left:50%;bottom:calc(var(--pl-s-8) + env(safe-area-inset-bottom,0px));transform:translateX(-50%);z-index:var(--pl-z-toast);display:flex;flex-direction:column-reverse;align-items:center;max-width:calc(100% - var(--pl-s-6) * 2);pointer-events:none}
.pl-toasts--tabbar{bottom:calc(var(--pl-tabbar-h) + var(--pl-s-6) + env(safe-area-inset-bottom,0px))}
.pl-toasts__lane{display:flex;flex-direction:column-reverse;align-items:center;gap:var(--pl-s-3);max-width:100%}
.pl-toasts__lane:not(:empty)+.pl-toasts__lane:not(:empty){margin-bottom:var(--pl-s-3)}
.pl-toast{pointer-events:auto;display:flex;align-items:center;gap:14px;max-width:calc(100vw - var(--pl-s-6) * 2);padding:10px 10px 10px var(--pl-s-6);background:var(--pl-ink);color:var(--pl-on-primary);border-radius:var(--pl-radius-2);box-shadow:var(--pl-shadow-toast);font-family:var(--pl-font-sans);font-size:var(--pl-fs-body);line-height:var(--pl-lh-snug)}
.pl-toast__flag{flex:none;width:var(--pl-fs-lead);height:var(--pl-fs-lead);color:var(--pl-amber-200)}
.pl-toast__flag svg{display:block;width:100%;height:100%}
.pl-toast__body{flex:1 1 auto;min-width:0;display:flex;flex-direction:column;gap:var(--pl-s-1)}
.pl-toast__title{font-weight:var(--pl-w-semi)}
.pl-toast__msg{font-weight:var(--pl-w-regular)}
.pl-toast__act{flex:none;display:flex;align-items:center}
.pl-toast__action{flex:none;height:auto;min-height:var(--pl-control-h-sm);padding:0 10px;border:var(--pl-border-w) solid var(--pl-chrome-line-strong);border-radius:var(--pl-radius-1);background:transparent;color:var(--pl-on-primary);font-family:var(--pl-font-sans);font-size:var(--pl-fs-small);font-weight:var(--pl-w-semi);line-height:1;white-space:nowrap;cursor:pointer}
.pl-toast__action:hover{background:var(--pl-chrome-hover-strong)}
.pl-toast__act .pl-btn{color:var(--pl-on-primary);border-color:var(--pl-chrome-line-strong);background:transparent}
.pl-toast__act .pl-btn:hover:not(:disabled){color:var(--pl-on-primary);border-color:var(--pl-chrome-line-strong);background:var(--pl-chrome-hover-strong)}
@media (max-width:767px){/* --pl-bp-tablet */
.pl-toasts{left:var(--pl-s-4);right:var(--pl-s-4);transform:none;max-width:none}}
`;
  document.head.appendChild(s);
};

/* The same triangle MoneyCell flies, so an estimate looks identical
   everywhere. --pl-amber-600 is the estimate colour on light surfaces; it
   fails contrast on the toast's ink background, so the flag uses the lighter
   step of the same ramp here instead (11.8:1 on ink). */
const TRI = (
  <svg viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
    <path d="M8 1.6 15 14H1L8 1.6Zm0 3.5a.85.85 0 0 0-.85.95l.3 3.1a.55.55 0 0 0 1.1 0l.3-3.1A.85.85 0 0 0 8 5.1Zm0 6.1a.85.85 0 1 0 0 1.7.85.85 0 0 0 0-1.7Z" />
  </svg>
);

/* The stack's hotkey, so a Toast inside it can announce it on its action
   (aria-keyshortcuts). Empty outside a ToastStack. */
const HotkeyCtx = React.createContext('');

const isError = (k) => React.isValidElement(k) && k.props && k.props.tone === 'error';

/**
 * The always-mounted live regions for the app's toasts (see Toast.d.ts).
 * Bottom centre, one toast at a time in the redesign (D-13). Errors go into
 * a `role="alert"` lane, everything else into a polite `role="status"` lane:
 * two sibling regions, never one nested in the other, so a screen reader
 * announces each toast once. F8 (the `hotkey`) moves focus to the newest
 * toast's action, and Esc from there puts it back, so the chef can reach
 * Undo from the keyboard before the toast times out.
 */
export function ToastStack({ label = 'Notifications (F8)', offset = 'default', hotkey = 'F8',
                             children, className = '', onKeyDown, ...rest }) {
  ensure();
  const ref = React.useRef(null);
  const back = React.useRef(null);

  React.useEffect(() => {
    if (!hotkey || typeof document === 'undefined') return undefined;
    const onKey = (e) => {
      if (e.key !== hotkey || e.defaultPrevented) return;
      const root = ref.current;
      // An open dialog owns the keyboard; do not pull focus out of it.
      if (!root || document.querySelector('[aria-modal="true"]')) return;
      const targets = root.querySelectorAll('button:not(:disabled),a[href]');
      if (!targets.length) return;
      e.preventDefault();
      if (!root.contains(document.activeElement)) back.current = document.activeElement;
      targets[targets.length - 1].focus();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [hotkey]);

  // The focused toast went away (Undo ran, or it was replaced): put focus
  // back where F8 found it instead of leaving it on the document body.
  React.useEffect(() => {
    const prev = back.current;
    if (!prev || typeof document === 'undefined') return;
    const a = document.activeElement;
    if (ref.current && ref.current.contains(a)) return;
    back.current = null;
    if ((!a || a === document.body) && document.contains(prev) && typeof prev.focus === 'function') {
      prev.focus();
    }
  });

  const keyDown = (e) => {
    if (onKeyDown) onKeyDown(e);
    if (e.key !== 'Escape' || e.defaultPrevented || !back.current) return;
    const prev = back.current;
    back.current = null;
    if (document.contains(prev) && typeof prev.focus === 'function') {
      e.preventDefault();
      prev.focus();
    }
  };

  const cls = ['pl-toasts', offset === 'tabbar' ? 'pl-toasts--tabbar' : '', className]
    .filter(Boolean).join(' ');
  const kids = React.Children.toArray(children);
  return (
    <HotkeyCtx.Provider value={hotkey || ''}>
      <div ref={ref} className={cls} onKeyDown={keyDown} {...rest}>
        <div className="pl-toasts__lane" role="alert">{kids.filter(isError)}</div>
        <div className="pl-toasts__lane" role="status" aria-live="polite" aria-atomic="false"
             aria-label={label}>
          {kids.filter((k) => !isError(k))}
        </div>
      </div>
    </HotkeyCtx.Provider>
  );
}

/**
 * One toast: an ink pill, bottom-centre, that says what just happened. No
 * dismiss control by design (D-13): it goes away on its own timer, which
 * pauses while the pointer is over it or focus is in it. Pass `onTimeout`
 * (the caller removes the toast there) to run the timer; `duration` defaults
 * to 5s, 8s for `tone="error"`. Pass `actionLabel` + `onAction` for the single
 * allowed action (usually "Undo"). See Toast.d.ts.
 */
export function Toast({ tone = 'default', title, message, estimate = false,
                        action, actionLabel, onAction, onDismiss, dismissLabel,
                        duration, onTimeout, children, className = '',
                        onPointerEnter, onPointerLeave, onFocus, onBlur, ...rest }) {
  ensure();
  // onDismiss/dismissLabel are accepted only so existing callers (30+ call
  // sites through lib/toast.tsx) keep compiling: the redesign has no visible
  // dismiss control, so neither prop renders anything here.
  void onDismiss;
  void dismissLabel;
  const hotkey = React.useContext(HotkeyCtx);

  const ms = duration == null ? (tone === 'error' ? 8000 : 5000) : duration;
  const timed = typeof onTimeout === 'function' && ms > 0;
  const [hover, setHover] = React.useState(false);
  const [focus, setFocus] = React.useState(false);
  const left = React.useRef(ms);
  const cb = React.useRef(onTimeout);
  cb.current = onTimeout;

  // A new duration starts the count again. Declared before the timer so it
  // runs first on the render that changes it.
  React.useEffect(() => { left.current = ms; }, [ms]);

  // WCAG 2.2.1: the countdown stops while the chef is reading (hover) or
  // working (focus), and resumes with what was left, never less than 1s.
  React.useEffect(() => {
    if (!timed || hover || focus) return undefined;
    const t0 = Date.now();
    const id = setTimeout(() => { if (cb.current) cb.current(); }, left.current);
    return () => {
      clearTimeout(id);
      left.current = Math.max(1000, left.current - (Date.now() - t0));
    };
  }, [timed, ms, hover, focus]);

  const cls = ['pl-toast', className].filter(Boolean).join(' ');
  const body = message != null ? (
    <span className="pl-toast__msg">{message}</span>
  ) : (
    <React.Fragment>
      {title ? <span className="pl-toast__title">{title}</span> : null}
      {children ? <span className="pl-toast__msg">{children}</span> : null}
    </React.Fragment>
  );
  return (
    <div className={cls} data-pl-dark=""
         onPointerEnter={(e) => { setHover(true); if (onPointerEnter) onPointerEnter(e); }}
         onPointerLeave={(e) => { setHover(false); if (onPointerLeave) onPointerLeave(e); }}
         onFocus={(e) => { setFocus(true); if (onFocus) onFocus(e); }}
         onBlur={(e) => {
           if (!e.currentTarget.contains(e.relatedTarget)) setFocus(false);
           if (onBlur) onBlur(e);
         }}
         {...rest}>
      {estimate ? (
        <span className="pl-toast__flag" role="img" aria-label="Estimated"
              title="Estimated: at least one ingredient has no price or size set">
          {TRI}
        </span>
      ) : null}
      <span className="pl-toast__body">{body}</span>
      {actionLabel ? (
        <span className="pl-toast__act">
          <button type="button" className="pl-toast__action" onClick={onAction}
                  aria-keyshortcuts={hotkey || undefined}>{actionLabel}</button>
        </span>
      ) : action ? (
        <span className="pl-toast__act">{action}</span>
      ) : null}
    </div>
  );
}
